import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { ExperienceCategory } from "@intelligems/ig-types";
import { resetAgProducts } from "@src/store/slices/agProducts";
import { resetAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import { setUseAppPadding } from "@src/store/slices/appSettings";
import { resetAudiences } from "@src/store/slices/audiences";
import { resetCartTargeting } from "@src/store/slices/cartTargeting";
import { resetCheckoutBlocks } from "@src/store/slices/checkoutBlocks";
import { clearDashboardFilters } from "@src/store/slices/dashboardFilters";
import { resetDraftExperience } from "@src/store/slices/draftExperience";
import { resetExclusionGroups } from "@src/store/slices/exclusionGroups";
import { resetComponents } from "@src/store/slices/experienceComponents";
import { resetExperienceInfoState } from "@src/store/slices/experienceInfoState";
import { resetExperienceMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { resetExperienceProducts } from "@src/store/slices/experienceProducts";
import { resetExperienceProductTargeting } from "@src/store/slices/experienceProductTargeting";
import { resetExperience } from "@src/store/slices/experiences";
import { resetOfferProducts } from "@src/store/slices/offerProducts";
import { resetOffer } from "@src/store/slices/offers";
import { resetOnsiteEdits } from "@src/store/slices/onsiteEdits";
import { resetOnsiteInjections } from "@src/store/slices/onsiteInjections";
import { resetPageTargeting } from "@src/store/slices/pageTargeting";
import { closeProductPicker } from "@src/store/slices/productPicker";
import { resetRedirects } from "@src/store/slices/redirects";
import { resetUpsellProductRules } from "@src/store/slices/upsellProductRules";
import { resetUserInterfaces } from "@src/store/slices/userInterfaces";
import { resetVariations } from "@src/store/slices/variations";
import { resetWidgets } from "@src/store/slices/widgets";

import { isSyntheticCategorySwapPending } from "./experience-route-swap-flag";

export const useClearExperienceOnUnmount = (category: ExperienceCategory) => {
  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      // `useRedirectOnCategorySwitch` unmounts this route as a side effect
      // of swapping to the other category's route tree for the SAME
      // experience — not a real exit. Wiping here would destroy the draft
      // mid-swap before the new route can pick it up. A peek, not a
      // consuming read: `LoggedInProvider`'s pathname effect (an ancestor)
      // checks the same flag later in this same commit and needs it to
      // still be true — `useRedirectOnCategorySwitch` clears it out-of-band
      // once the whole commit has settled.
      if (isSyntheticCategorySwapPending()) return;

      dispatch(resetExperience());
      dispatch(resetRedirects());
      dispatch(resetExclusionGroups());
      dispatch(resetWidgets());
      dispatch(resetUserInterfaces());
      dispatch(resetOnsiteInjections());
      dispatch(resetOnsiteEdits());
      dispatch(resetComponents());
      dispatch(resetPageTargeting());
      dispatch(resetExperienceProductTargeting());
      dispatch(resetAudiences());
      dispatch(resetExperienceProducts());
      dispatch(resetOfferProducts());
      dispatch(resetOffer());
      dispatch(resetCartTargeting());
      dispatch(resetUpsellProductRules());
      if (category === "experiment") dispatch(resetDraftExperience());
      dispatch(resetVariations());
      dispatch(setUseAppPadding(true));
      dispatch(resetExperienceInfoState());
      dispatch(closeProductPicker());
      dispatch(resetAnalyticsProducts());
      dispatch(clearDashboardFilters());
      dispatch(resetExperienceMetrics());
      dispatch(resetAgProducts());
      dispatch(resetCheckoutBlocks());
    };
  }, []);
};
